import React, { ChangeEvent } from 'react';
import clsx from 'clsx';
import { FieldError } from '@spike/model';
import InputMask from 'react-input-mask';
import styles from './PhoneField3.module.scss';
import { Label } from './V3/Forms/Label/Label';
import { useMarketplace } from 'hooks';
import { cleanPhoneNumberFormat } from '@spike/phone-utils';

export interface PhoneField3Props {
    id?: string;
    name?: string;
    label?: string | JSX.Element;
    value: string;
    placeholder?: string;
    errors?: Array<FieldError>;
    onChange: (value: string, name?: string) => void;
    onBlur?: () => void;
    className?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    autoComplete?: 'off' | 'on' | 'nope';
    required?: boolean;
    information?: string;
}

export const PhoneField3 = React.forwardRef<HTMLInputElement, PhoneField3Props>(
    (
        {
            id,
            name,
            label,
            value,
            placeholder,
            errors,
            onChange,
            onBlur,
            className,
            autoFocus,
            disabled,
            autoComplete = 'nope',
            required,
            information
        },
        ref
    ) => {
        const marketplace = useMarketplace();
        const isUSorCA = ['US', 'CA'].includes(marketplace.basics.address.country?.id.toUpperCase() || 'US');

        const errorMessage = errors?.find(error => error.fieldName === name)?.errorMessage;
        const hasError = !!errorMessage;

        const convertToNumbers = (inputValue: string): string => {
            return cleanPhoneNumberFormat(inputValue);
        };

        const handleUSChange = (event: ChangeEvent<HTMLInputElement>): void => {
            const cleanedValue = convertToNumbers(event.target.value);
            onChange(cleanedValue, name);
        };

        const handleOtherChange = (event: ChangeEvent<HTMLInputElement>): void => {
            const newValue = event.target.value;
            if (/^\+{0,1}[0-9]{0,12}$/.test(newValue)) {
                onChange(newValue, name);
            }
        };

        const inputClassName = clsx(styles.field, {
            [styles.emptyField]: !value || value.length === 0,
            [styles.fieldError]: hasError,
            [styles.disabledField]: disabled
        });

        const renderUSPhoneField = () => (
            <InputMask
                mask="(999) 999-9999"
                value={value}
                onChange={handleUSChange}
                onBlur={onBlur}
                disabled={disabled}
            >
                {(inputProps: {
                    ref: React.Ref<HTMLInputElement>;
                    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
                    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
                    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
                    value: string;
                }) => (
                    <input
                        {...inputProps}
                        ref={node => {
                            // Handle both InputMask's ref and forwarded ref
                            if (typeof inputProps.ref === 'function') {
                                inputProps.ref(node);
                            } else if (inputProps.ref) {
                                (inputProps.ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
                            }
                            if (typeof ref === 'function') {
                                ref(node);
                            } else if (ref) {
                                (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
                            }
                        }}
                        type="tel"
                        id={id ? `${id}_number` : id}
                        name={name}
                        placeholder={placeholder}
                        autoFocus={autoFocus}
                        className={inputClassName}
                        disabled={disabled}
                        autoComplete={autoComplete}
                    />
                )}
            </InputMask>
        );

        const renderOtherPhoneField = () => (
            <input
                ref={ref}
                type="tel"
                id={id ? `${id}_number` : id}
                name={name}
                placeholder={placeholder || 'Phone Number'}
                autoFocus={autoFocus}
                className={inputClassName}
                disabled={disabled}
                autoComplete={autoComplete}
                onChange={handleOtherChange}
                onBlur={onBlur}
                value={value}
                maxLength={13}
            />
        );

        return (
            <div className={clsx(styles.container, className)}>
                {label && (
                    <Label
                        htmlFor={id ? `${id}_number` : id}
                        error={errorMessage || undefined}
                        required={required}
                    >
                        {label}
                    </Label>
                )}
                {isUSorCA ? renderUSPhoneField() : renderOtherPhoneField()}
            </div>
        );
    }
);

PhoneField3.displayName = 'PhoneField3';

export default PhoneField3;
